import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { toast } from 'sonner';

import { Loader } from '@/components/Loader';
import { useMy } from '@/hooks';
import {
  ORGANIZATION_DISPLAY_NAME_KEY,
  ORGANIZATION_TENANT_PROFILE_PICTURE_ID,
  useGetProfilePictureUrl,
} from '@/hooks/profilePicture';
import { useUploadFile } from '@/hooks/useFiles';
import { useUpdateSetting } from '@/hooks/useSetting';
import {
  OrgProfileEditForm,
  SettingEditableCard,
} from '@/sections/settings/components';
import { useAuth } from '@/state/auth';
import { cn } from '@/utils/classname';

interface OrgProfileSectionProps {
  email?: string;
  editable?: boolean;
}

/**
 * Organization profile section displaying profile picture and organization name.
 *
 * Features:
 * - Profile picture upload/delete with visual loading states
 * - Organization name editing with validation
 * - Edit modal for profile changes
 * - Impersonation support via email prop
 *
 * React 19 Patterns:
 * - Standard state management for upload tracking
 * - Optimistic updates handled by parent components
 *
 * Used in:
 * - OrganizationSettingsCards
 */
export const OrgProfileSection: React.FC<OrgProfileSectionProps> = ({
  email,
  editable = true,
}) => {
  const { me, friend } = useAuth();
  const currentEmail = email || friend || me;

  // Profile picture and organization name
  const { data: settings, refetch: refetchSettings } = useMy(
    {
      resource: 'setting',
    },
    {
      email: currentEmail,
      staleTime: 30000,
      gcTime: 300000,
    }
  );

  const displayNameSetting = settings?.find(
    s => s.name === ORGANIZATION_DISPLAY_NAME_KEY
  );
  const organizationName =
    typeof displayNameSetting?.value === 'string'
      ? displayNameSetting.value
      : '';

  const { data: profilePictureUrl, isFetching: isProfilePictureFetching } =
    useGetProfilePictureUrl({
      email: currentEmail,
      isOrganization: true,
    });

  const { mutateAsync: updateSetting } = useUpdateSetting(
    currentEmail !== me ? currentEmail : undefined
  );

  const { mutate: uploadFile, status: uploadFileStatus } =
    useUploadFile(currentEmail);

  const isProfilePictureLoading =
    uploadFileStatus === 'pending' || isProfilePictureFetching;

  /**
   * Handles saving organization display name with settings refetch for immediate UI update
   * Called as fire-and-forget from OrgProfileEditForm (optimistic close pattern)
   */
  const handleSaveDisplayName = async (newValue: string) => {
    try {
      await updateSetting({
        name: ORGANIZATION_DISPLAY_NAME_KEY,
        value: newValue,
        silent: true,
      });
      await refetchSettings();
      toast.success('Organization name updated successfully');
    } catch (error) {
      console.error('Failed to save organization name:', error);
      toast.error('Failed to update organization name. Please try again.');
    }
  };

  return (
    <SettingEditableCard
      data-testid="org-profile-card"
      editConfig={{
        modalTitle: 'Edit Organization Profile',
        formComponent: OrgProfileEditForm,
        formProps: {
          currentName: organizationName,
          currentPictureUrl: profilePictureUrl?.url,
          onUploadPicture: (buffer: ArrayBuffer) => {
            uploadFile({
              name: ORGANIZATION_TENANT_PROFILE_PICTURE_ID,
              content: buffer,
            });
          },
          onDeletePicture: () => {
            uploadFile({
              name: ORGANIZATION_TENANT_PROFILE_PICTURE_ID,
              content: '',
            });
          },
          onSaveName: handleSaveDisplayName,
          isUploadingPicture: uploadFileStatus === 'pending',
        },
        modalSize: 'lg',
      }}
      editable={editable}
    >
      {({ openModal }) => (
        <div className="flex items-center gap-4">
          {/* Clickable organization logo */}
          {isProfilePictureLoading ? (
            <div className="border-chariot-stroke flex size-12 flex-col items-center justify-center gap-3 rounded-lg border-[3px] border-dashed">
              <Loader isLoading type="spinner" />
            </div>
          ) : (
            <div
              onClick={editable ? openModal : undefined}
              onKeyDown={
                editable
                  ? e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openModal();
                      }
                    }
                  : undefined
              }
              role={editable ? 'button' : undefined}
              tabIndex={editable ? 0 : undefined}
              aria-label="Click to edit organization profile"
              className={cn(
                'shrink-0',
                editable &&
                  'focus:ring-chariot-brand-primary cursor-pointer rounded-lg transition-opacity hover:opacity-80 focus:ring-2 focus:ring-offset-2 focus:outline-none'
              )}
            >
              {profilePictureUrl?.url ? (
                <img
                  src={profilePictureUrl.url}
                  alt="Organization Profile"
                  className="border-chariot-stroke size-12 rounded-lg border object-cover"
                />
              ) : (
                <div className="border-chariot-stroke bg-chariot-background-secondary hover:bg-chariot-background-tertiary flex size-12 items-center justify-center rounded-lg border transition-colors">
                  <BuildingOfficeIcon className="text-chariot-text-secondary size-6" />
                </div>
              )}
            </div>
          )}
          <div className="flex flex-col gap-1">
            <h4 className="font-bold">Organization Profile</h4>
            <span className="text-chariot-text-secondary text-xs">
              {organizationName || currentEmail}
            </span>
          </div>
        </div>
      )}
    </SettingEditableCard>
  );
};
